/**
 * AI CRM API Service
 * Handles all API calls to the AI CRM backend
 */

import {
  Lead,
  DashboardSummary,
  LeadFilters,
  CreateLeadInput,
  UpdateLeadInput,
  PaginatedResponse,
  ApiResponse
} from '../types';
import { apiClient } from './apiClient';

// AI Content Generation APIs
export const aiContentApi = {
  /**
   * Generate email content with AI assistance
   */
  generateEmailContent: async (data: {
    purpose: 'campaign_details' | 'email_content' | 'subject_lines';
    prompt: string;
    context?: {
      recipientType?: string;
      industry?: string;
      tone?: string;
    };
  }): Promise<ApiResponse<{
    suggestions?: string[];
    subject?: string;
    htmlContent?: string;
  }>> => {
    const response = await apiClient.post<ApiResponse<any>>('/ai/generate-email-content', data);
    return response.data;
  },

  /**
   * Get AI suggestions for campaign timing
   */
  getOptimalSendTime: async (data: {
    recipientType: string;
    timezone?: string;
  }): Promise<ApiResponse<{
    recommendedTime: string;
    reasoning: string;
  }>> => {
    const response = await apiClient.post<ApiResponse<any>>('/ai/optimal-send-time', data);
    return response.data;
  },
};

// Dashboard APIs
export const dashboardApi = {
  /**
   * Get dashboard summary statistics
   */
  getSummary: async (): Promise<DashboardSummary> => {
    const response = await apiClient.get<ApiResponse<DashboardSummary>>('/dashboard/summary');
    return response.data.data;
  }
};

// Lead APIs
export const leadApi = {
  /**
   * Get all leads with optional filters
   */
  getLeads: async (filters?: LeadFilters & { limit?: number; offset?: number }): Promise<PaginatedResponse<Lead>> => {
    const response = await apiClient.get<PaginatedResponse<Lead>>('/leads', {
      params: filters
    });
    return response.data;
  },

  /**
   * Get lead by ID
   */
  getLeadById: async (id: string): Promise<Lead> => {
    const response = await apiClient.get<ApiResponse<Lead>>(`/leads/${id}`);
    return response.data.data;
  },

  /**
   * Create a new lead
   */
  createLead: async (data: CreateLeadInput): Promise<Lead> => {
    const response = await apiClient.post<ApiResponse<Lead>>('/leads', data);
    return response.data.data;
  },

  /**
   * Update a lead
   */
  updateLead: async (id: string, data: UpdateLeadInput): Promise<Lead> => {
    const response = await apiClient.put<ApiResponse<Lead>>(`/leads/${id}`, data);
    return response.data.data;
  },

  /**
   * Delete a lead
   */
  deleteLead: async (id: string): Promise<void> => {
    await apiClient.delete(`/leads/${id}`);
  },

  /**
   * Update lead status
   */
  updateLeadStatus: async (id: string, status: string): Promise<Lead> => {
    const response = await apiClient.put<ApiResponse<Lead>>(`/leads/${id}/status`, { status });
    return response.data.data;
  },

  /**
   * Update lead priority
   */
  updateLeadPriority: async (id: string, priority: string): Promise<Lead> => {
    const response = await apiClient.put<ApiResponse<Lead>>(`/leads/${id}/priority`, { priority });
    return response.data.data;
  },

  /**
   * Get lead activities
   */
  getLeadActivities: async (id: string): Promise<any[]> => {
    const response = await apiClient.get<ApiResponse<any[]>>(`/leads/${id}/activities`);
    return response.data.data;
  },

  /**
   * Add activity to lead
   */
  addLeadActivity: async (id: string, activity: any): Promise<any> => {
    const response = await apiClient.post<ApiResponse<any>>(`/leads/${id}/activities`, activity);
    return response.data.data;
  },

  /**
   * Convert lead to customer
   */
  convertLead: async (id: string): Promise<{ lead: Lead; customer: any }> => {
    const response = await apiClient.post<ApiResponse<{ lead: Lead; customer: any }>>(`/leads/${id}/convert`);
    return response.data.data;
  },

  /**
   * Mark a lead's email as verified by the user
   */
  markEmailVerified: async (id: string): Promise<Lead> => {
    const response = await apiClient.patch<ApiResponse<Lead>>(`/leads/${id}/verify-email`);
    return response.data.data;
  },

  /**
   * Trigger AI scoring for a lead
   */
  scoreLead: async (id: string): Promise<Lead> => {
    const response = await apiClient.post<ApiResponse<Lead>>(`/leads/${id}/score`);
    return response.data.data;
  },
};

// Import Client APIs
import { clientApi, orderApi } from './clientApi';

// Import Lead Generation APIs
import { leadGenerationApi } from './leadGenerationApi';

// Import Email Campaign APIs
import { emailTemplateApi, emailCampaignApi, emailDomainApi } from './emailCampaignApi';

// Import AI Assistant APIs
import { aiAssistantApi } from './aiAssistantApi';

// Import Subscription APIs
import { subscriptionApi } from './subscriptionApi';

// Export all APIs
export const aiCrmApi = {
  dashboard: dashboardApi,
  leads: leadApi,
  clients: clientApi,
  orders: orderApi,
  leadGeneration: leadGenerationApi,
  emailTemplates: emailTemplateApi,
  emailCampaigns: emailCampaignApi,
  emailDomains: emailDomainApi,
  aiAssistant: aiAssistantApi,
  subscriptions: subscriptionApi,
  aiContent: aiContentApi,
  // Direct access methods
  generateEmailContent: aiContentApi.generateEmailContent,
  getOptimalSendTime: aiContentApi.getOptimalSendTime,
};

// Export apiClient for use in other API files
export { apiClient };

export default aiCrmApi;
